import { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { Transaction } from "./types";
import { buildPreviewXlsx } from "./previewXlsx";

/** Общие строки для обеих выгрузок предпросмотра. */
const EXPORT_ROWS = [
  [
    "id",
    "merchant_order_id",
    "amount_start",
    "amount_paid",
    "currency",
    "status",
    "transaction_type",
    "created_at",
  ],
  [
    "tx_preview_001",
    "ORD-4471",
    "15000",
    "15000",
    "rub",
    "completed",
    "deposit",
    "12.08.2026 10:14:02",
  ],
  [
    "tx_preview_002",
    "ORD-4472",
    "8200",
    "0",
    "rub",
    "canceled",
    "deposit",
    "12.08.2026 10:41:55",
  ],
];

const buildPreviewExport = (format: "csv" | "xlsx"): Blob => {
  if (format === "xlsx") {
    return buildPreviewXlsx(EXPORT_ROWS, "Transactions");
  }
  return new Blob([EXPORT_ROWS.map((row) => row.join(",")).join("\n") + "\n"], {
    type: "text/csv",
  });
};


/**
 * Демонстрационные данные для локального просмотра кабинета мерчанта
 * без бэкенда и авторизации. Включается через REACT_APP_UI_PREVIEW=true
 * (см. scripts/start-preview.js). В обычном режиме адаптер не подключается.
 */

const previewUser = {
  id: 4400,
  username: "preview.merchant",
  role: "merchant",
  name: "Aurora Market",
};

const statuses: Transaction["status"][] = [
  "completed",
  "created",
  "dispute",
  "canceled",
];

const transactions: Transaction[] = Array.from({ length: 46 }, (_, index) => {
  const createdAt = new Date(Date.now() - index * 41 * 60 * 1000);
  const status = statuses[index % statuses.length];
  const amount = 4_200 + index * 940;

  return {
    merchant_order_id: `AM-${String(77300 + index)}`,
    user_id: `user-${String(1180 + index)}`,
    fullname: ["Анна Смирнова", "Иван Петров", "Мария Орлова"][index % 3],
    id: `fz-merch-${String(index + 1).padStart(4, "0")}`,
    number: `2200 •••• •••• ${String(5100 + index)}`,
    status,
    bank_name: ["Альфа-Банк", "Т-Банк", "Сбербанк"][index % 3],
    our_fee: Math.round(amount * 0.028),
    merch_id: 1,
    start_amount: amount,
    paid_amount: status === "completed" ? amount : 0,
    created_at: createdAt.toISOString(),
    transaction_type: index % 4 === 0 ? "payout" : "deposit",
    currency: "RUB",
    updated_at:
      status === "created"
        ? null
        : new Date(createdAt.getTime() + 9 * 60 * 1000).toISOString(),
    payment_method: index % 2 === 0 ? "rub_fps" : "rub_card_number",
    our_rate: 7.5,
    user_ip: `10.44.12.${18 + index}`,
    qr_code: null,
  };
});

/** Плоский список ставок — так его отдаёт /rates/merchant. */
const rates = [
  ["deposit", "rub_fps", 8.2, 10_000, 15_000],
  ["deposit", "rub_card_number", 8.6, 10_000, 15_000],
  ["deposit", "rub_fps", 7.6, 15_000, 50_000],
  ["deposit", "rub_card_number", 8.0, 15_000, 50_000],
  ["deposit", "rub_card_number", 7.1, 50_000, 100_000],
  ["payout", "rub_card_number", 5.9, 10_000, 15_000],
  ["payout", "rub_card_number", 5.4, 15_000, 50_000],
  ["payout", "rub_fps", 4.9, 50_000, 100_000],
  ["cashin", "rub_card_number", 6.8, 10_000, 15_000],
  ["cashin", "rub_fps", 6.3, 15_000, 50_000],
  ["withdrawal", "rub_fps", 5.1, 10_000, 15_000],
  ["withdrawal", "rub_card_number", 4.7, 15_000, 50_000],
].map(([operation_type, payment_method, rate, minsum, maxsum]) => ({
  operation_type: operation_type as string,
  payment_method: payment_method as string,
  currency: "rub",
  rate: rate as number,
  merchant_id: 1,
  minsum: minsum as number,
  maxsum: maxsum as number,
}));

/** Баланс мерчанта — один объект, не разбитый по мерчантам. */
const balance = {
  deposit: { fiat: { rub: 1_284_500, kzt: 742_000 }, usdt: 18_540 },
  payout: { fiat: { rub: 623_800, kzt: 391_500 }, usdt: 8_230 },
  frozen: { fiat: { rub: 84_200, kzt: 48_600 }, usdt: 947 },
};

const webhooks = {
  deposit: "https://aurora-market.example/webhooks/finzora/deposit",
  payout: "https://aurora-market.example/webhooks/finzora/payout",
};

const normalizePath = (config: InternalAxiosRequestConfig) =>
  (config.url || "")
    .trim()
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/api/, "")
    .replace(/\/+$/, "");

const makeResponse = (
  config: InternalAxiosRequestConfig,
  data: unknown,
): AxiosResponse => ({
  data,
  status: 200,
  statusText: "OK",
  headers: { "x-finzora-preview": "true" },
  config,
  request: { preview: true },
});

/**
 * Two-factor state for preview: kept in memory, any six digits pass as a code.
 * Enough to walk the whole setup wizard and see every state of the section
 * without a backend.
 */
const twoFactor = {
  enabled: false,
  pendingSecret: null as string | null,
};

const previewTotpValid = (code: unknown) => /^\d{6}$/.test(String(code ?? ""));

const getPreviewData = (config: InternalAxiosRequestConfig) => {
  const path = normalizePath(config);

  // Export returns a real Blob, otherwise the page treats the response as empty.
  // The format is honoured for real: csv goes out as text, xlsx as a valid
  // workbook — Excel refuses to open csv bytes under an .xlsx name.
  if (path === "/merchants/export") {
    const body = config.data ? JSON.parse(config.data) : {};
    return buildPreviewExport(body.format === "xlsx" ? "xlsx" : "csv");
  }

  if (path.startsWith("/auth/2fa")) {
    const body = config.data ? JSON.parse(config.data) : {};

    if (path === "/auth/2fa/status") {
      return {
        enabled: twoFactor.enabled,
        pending_setup: Boolean(twoFactor.pendingSecret) && !twoFactor.enabled,
      };
    }
    if (path === "/auth/2fa/setup") {
      twoFactor.pendingSecret = "JBSWY3DPEHPK3PXPPREVIEWDEMO2FA00";
      return {
        secret: twoFactor.pendingSecret,
        otpauth_url: `otpauth://totp/Finzora:preview.merchant?secret=${twoFactor.pendingSecret}&issuer=Finzora&algorithm=SHA1&digits=6&period=30`,
        issuer: "Finzora",
      };
    }
    if (path === "/auth/2fa/enable") {
      if (!previewTotpValid(body.code)) {
        return { __status: 400, detail: "Invalid code" };
      }
      twoFactor.enabled = true;
      return { enabled: true };
    }
    if (path === "/auth/2fa/disable") {
      if (!previewTotpValid(body.code)) {
        return { __status: 400, detail: "Invalid code" };
      }
      twoFactor.enabled = false;
      twoFactor.pendingSecret = null;
      return { enabled: false };
    }
  }

  if (path === "/auth/login") {
    return twoFactor.enabled
      ? { details: "two-factor code required", twofa_required: true }
      : { details: "login successful", twofa_required: false, user: previewUser };
  }
  if (path === "/auth/login/2fa") {
    const body = config.data ? JSON.parse(config.data) : {};
    if (!previewTotpValid(body.code)) {
      return { __status: 401, detail: "Invalid code" };
    }
    return { details: "login successful", user: previewUser };
  }
  if (path === "/me") {
    return { user: previewUser };
  }
  if (path === "/auth/change_password") return { ok: true };
  if (path === "/rates/merchant") return rates;
  if (path === "/merchants/balance") return balance;
  if (path === "/callback_url/merchant") return webhooks;

  if (path === "/transactions/get_list_merchant") {
    const page = Number((config.data && JSON.parse(config.data)?.page) || 1);
    const pageSize = 20;
    return {
      items: transactions.slice((page - 1) * pageSize, page * pageSize),
      total_count: transactions.length,
      total_pages: Math.ceil(transactions.length / pageSize),
    };
  }

  if (path === "/transactions/get_order_merchant") {
    const query = String(
      (config.data && JSON.parse(config.data)?.filters?.search_query) || "",
    )
      .trim()
      .toLowerCase();

    if (!query) return transactions;
    return transactions.filter(
      (transaction) =>
        transaction.id.toLowerCase().includes(query) ||
        transaction.merchant_order_id.toLowerCase().includes(query),
    );
  }

  return { ok: true, preview: true };
};

export const previewAdapter: AxiosAdapter = async (config) => {
  await new Promise((resolve) => window.setTimeout(resolve, 180));
  const data = getPreviewData(config) as any;

  // A fixture can ask for an error response — otherwise preview cannot show
  // how the page reacts to an invalid code or a lockout.
  if (data && typeof data === "object" && "__status" in data) {
    const { __status: status, ...rest } = data;
    const error: any = new Error(`Request failed with status code ${status}`);
    error.isAxiosError = true;
    error.config = config;
    error.response = {
      data: rest,
      status,
      statusText: "Error",
      headers: { "x-finzora-preview": "true" },
      config,
    };
    throw error;
  }

  return makeResponse(config, data);
};
